import { CheckCircle2, XCircle, FileText, Eye } from 'lucide-react'
import type { DirectorWorkflowItem } from '@/types/director'
import { ABSENCE_TYPE_LABELS, GATE_TRIGGER_LABELS, WORKFLOW_COLUMN_COLORS } from '@/types/director'

interface DirectorCardProps {
  item: DirectorWorkflowItem
  onOpen: () => void
  onApprove: () => void
  onReject: () => void
  onRequestDocs: () => void
  onPreviewDocs?: () => void
  onAbortSearch?: () => void
  onReadNotice?: () => void
  requestingDocs?: boolean
  canRequestDocs?: boolean
}

export function DirectorCard({
  item, onOpen, onApprove, onReject, onRequestDocs, onPreviewDocs,
  onAbortSearch, onReadNotice,
  requestingDocs = false, canRequestDocs = true,
}: DirectorCardProps) {
  const isNotice = item.kind === 'notice'
  const isRetro = item.kind === 'retro'
  const searchRunning = Boolean(
    item.gate_skipped_by_admin
    || (item.created_on_behalf && (item.status === 'pending' || item.status === 'partially_filled')),
  )
  const payerOnly = isRetro || searchRunning || (
    !item.has_decision
    && item.status !== 'director_pending'
    && item.status !== 'director_returned'
    && item.status !== 'notice'
  )
  const hasFile = Boolean(item.has_document ?? item.documents_received)
  const docsAlreadyOpen = Boolean(item.documents_requested_at) && !hasFile
  const requestDocsDisabled = requestingDocs || !canRequestDocs || docsAlreadyOpen
  const requestDocsTitle = !canRequestDocs
    ? 'Erfordert eine Rechte-Freigabe durch den Superadmin.'
    : docsAlreadyOpen
      ? 'Dokumente bereits angefordert'
      : requestingDocs
        ? 'Dokumente werden angefordert…'
        : 'Dokumente anfordern'
  return (
    <div
      onClick={isNotice || isRetro ? undefined : onOpen}
      className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border-l-4 ${WORKFLOW_COLUMN_COLORS[item.column]} p-3 hover:shadow-md transition-shadow ${isNotice || isRetro ? '' : 'cursor-pointer'}`}
      title={isNotice || isRetro ? undefined : 'Fall öffnen'}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <h4 className="text-sm font-semibold text-gray-900 truncate">{item.requesting_teacher_name}</h4>
          <p className="text-xs text-gray-500 mt-0.5">
            {item.lesson_count} Lektionen
          </p>
          {item.reason && (
            <p className="text-xs text-gray-500 mt-1 italic truncate" title={item.reason}>
              {item.reason}
            </p>
          )}
          {/* V5/C.2: Trigger-/Status-Badges (Props am Item vorhanden) */}
          <div className="flex flex-wrap gap-1 mt-1.5">
            {item.absence_type && item.absence_type !== 'other' && (
              <span className="px-1.5 py-0.5 text-[10px] rounded bg-gray-100 text-gray-600 font-medium">
                {/* Direktorkonfig v2 (D10): Snapshot-Label vorrangig */}
                {item.absence_type_label || ABSENCE_TYPE_LABELS[item.absence_type] || item.absence_type}
              </span>
            )}
            {(item.gate_triggers ?? []).map(t => (
              <span key={t} className="px-1.5 py-0.5 text-[10px] rounded bg-orange-50 text-orange-700 font-medium">
                {GATE_TRIGGER_LABELS[t] || t}
              </span>
            ))}
            {item.conditional_approval && (
              <span className="px-1.5 py-0.5 text-[10px] rounded bg-amber-50 text-amber-700 font-medium">
                Vorbehaltl. Abdeckung
              </span>
            )}
            {item.account_hold && (
              <span className="px-1.5 py-0.5 text-[10px] rounded bg-red-50 text-red-700 font-medium">
                Konto überschritten
              </span>
            )}
            {item.notice_badge && (
              <span className="px-1.5 py-0.5 text-[10px] rounded bg-slate-100 text-slate-700 font-medium">
                {item.notice_badge}
              </span>
            )}
            {item.created_on_behalf && (
              <span className="px-1.5 py-0.5 text-[10px] rounded bg-indigo-50 text-indigo-700 font-medium">
                Admin-Anfrage
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1 mt-3 pt-2 border-t border-gray-100">
        {isNotice ? (
          <button
            onClick={(e) => { e.stopPropagation(); onReadNotice?.() }}
            className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-slate-700 bg-slate-50 rounded hover:bg-slate-100 transition-colors"
            title="Gelesen"
          >
            <span>Gelesen</span>
          </button>
        ) : (
          <>
        <button
          onClick={(e) => { e.stopPropagation(); onApprove() }}
          className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-700 bg-green-50 rounded hover:bg-green-100 transition-colors"
          title="Zahler setzen"
        >
          <CheckCircle2 className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">{payerOnly ? 'Zahler' : 'Genehmigen'}</span>
        </button>
        {searchRunning ? (
        <button
          onClick={(e) => { e.stopPropagation(); onAbortSearch?.() }}
          className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-700 bg-red-50 rounded hover:bg-red-100 transition-colors"
          title="Suche abbrechen"
        >
          <XCircle className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Suche abbrechen</span>
        </button>
        ) : isRetro ? null : (
        <button
          onClick={(e) => { e.stopPropagation(); onReject() }}
          className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-700 bg-red-50 rounded hover:bg-red-100 transition-colors"
          title="Ablehnen"
        >
          <XCircle className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Ablehnen</span>
        </button>
        )}
          </>
        )}
        {!isNotice && hasFile && onPreviewDocs && (
          <button
            onClick={(e) => { e.stopPropagation(); onPreviewDocs() }}
            className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-700 bg-blue-50 rounded hover:bg-blue-100 transition-colors ml-auto"
            title="Dokumente ansehen"
          >
            <Eye className="w-3.5 h-3.5" />
          </button>
        )}
        {!isNotice && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); if (!requestDocsDisabled) onRequestDocs() }}
          disabled={requestDocsDisabled}
          className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-orange-700 bg-orange-50 rounded hover:bg-orange-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title={requestDocsTitle}
        >
          <FileText className="w-3.5 h-3.5" />
        </button>
        )}
      </div>
    </div>
  )
}
